/**
 * ProfileImageUpload Component
 *
 * Displays a profile image upload area.
 * - Empty state: Light gray rounded square with dashed border, camera icon + "Add Photo" text
 * - Uploading: Shows loading spinner
 * - With image: Circular profile image with hover overlay showing edit and delete buttons
 */

import { Loader2 } from "lucide-react"
import { ImageUploadIcon } from "@/components/icons"
import { getAssetUrl } from "@/utils/assets"
import { Button } from "../ui/button"

interface ProfileImageUploadProps {
  src?: string | null
  alt?: string
  onClick: () => void
  onDelete: () => void
  isUploading?: boolean
}

export function ProfileImageUpload({
  src,
  alt = "Profile photo",
  onClick,
  onDelete,
  isUploading,
}: ProfileImageUploadProps) {
  const uploadBg = getAssetUrl("upload-bg.jpeg")

  return (
    <div className="flex flex-col mb-6">
      {src && isUploading ? (
        /* With image + uploading: Show image with loading overlay */
        <div className="relative w-[140px] h-[140px] rounded-full overflow-hidden">
          <img src={src} alt={alt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          </div>
        </div>
      ) : src ? (
        /* With image: Circular profile with hover overlay for edit/delete */
        <div className="">
          <div className="relative w-[140px] h-[140px] md:w-[140px] md:h-[140px] group rounded-full overflow-hidden">
            <img src={src} alt={alt} className="w-full h-full object-cover" />
            {/* Hover overlay with edit and delete buttons - visible on hover */}
            <div className="hidden md:flex absolute inset-0 bg-[rgb(0,0,0,0.5)] opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3">
              <Button
                onClick={e => {
                  e.stopPropagation()
                  onClick()
                }}
                size={"sm"}
                variant={"primary"}
                className="bg-white px-5 py-0 rounded-full transition-colors"
              >
                Change
              </Button>
              <Button
                onClick={e => {
                  e.stopPropagation()
                  onDelete()
                }}
                size={"sm"}
                variant={"primary"}
                className="bg-white px-5 rounded-full bg-brand-terracotta text-white transition-colors"
              >
                Remove
              </Button>
            </div>
          </div>
          {/* Mobile: Change and Remove buttons below the image, visible on hover */}
          <div className="flex flex-row items-center justify-center gap-3 mt-3 md:hidden transition-opacity">
            <Button
              onClick={e => {
                e.stopPropagation()
                onClick()
              }}
              size={"sm"}
              variant={"primary"}
              className="bg-white px-5 py-0 rounded-full transition-colors"
            >
              Change
            </Button>
            <Button
              onClick={e => {
                e.stopPropagation()
                onDelete()
              }}
              size={"sm"}
              variant={"primary"}
              className="bg-white px-5 rounded-full bg-brand-terracotta text-white transition-colors"
            >
              Remove
            </Button>
          </div>
        </div>
      ) : isUploading ? (
        /* Loading state: Show spinner while uploading */
        <div className="relative w-[140px] h-[140px] border-2 rounded-full flex flex-col items-center justify-center overflow-hidden">
          <img src={uploadBg} alt="" className="absolute top-0 z-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[rgb(0,0,0,0.5)] z-10" />
          <Loader2 className="z-20 w-10 h-10 text-white animate-spin" />
        </div>
      ) : (
        /* Empty state: Light gray dashed box with camera + Add Photo text */
        <div
          className="relative w-[140px] h-[140px] border-2 rounded-full flex flex-col items-center justify-center cursor-pointer overflow-hidden"
          onClick={onClick}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onClick()
            }
          }}
        >
          <img src={uploadBg} alt="" className="absolute top-0 z-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[rgb(0,0,0,0.5)] z-10" />
          <ImageUploadIcon className="z-20" />
        </div>
      )}
    </div>
  )
}
